import { Link, useParams } from "react-router-dom";
import { ArrowLeft, XCircle } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/utils/formatDate";

import { SignatureTypesTable } from "../components/SignatureTypesTable";
import {
  useEmployeeAgreement,
  useTerminateAgreement,
} from "../hooks/useEmployeeAgreements";
import { usePageTitle } from "@/hooks/usePageTitle";

export function EmployeeAgreementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: ag, isLoading } = useEmployeeAgreement(id);
  usePageTitle(ag && `Umowa: ${ag.title}`);
  const { mutate: terminate, isPending: terminating } = useTerminateAgreement();
  const user = useAuthStore((s) => s.user);

  if (isLoading || !ag) return <PageLoader />;

  const canManage = !!user?.is_ceo;
  const isActive = ag.status === "active";

  return (
    <div className="p-3 sm:p-6 max-w-6xl mx-auto flex flex-col gap-4 sm:gap-6">
      {/* Breadcrumb */}
      <Link
        to="/ceo/employee-agreements"
        className="text-sm inline-flex items-center gap-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] w-fit"
      >
        <ArrowLeft size={14} /> Wróć do listy
      </Link>

      {/* Header card */}
      <section className="bg-white dark:bg-gray-800 border border-[var(--color-border)] p-4 sm:p-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-sm sm:text-lg font-mono uppercase tracking-wider break-words mb-2">
            {ag.title}
          </h1>
          <div className="flex items-center gap-1.5 flex-wrap mb-2">
            <Badge color={isActive ? "green" : "gray"}>
              {isActive ? "Aktywna" : "Rozwiązana"}
            </Badge>
            {ag.is_b2b && <Badge color="blue">B2B</Badge>}
          </div>
          <p className="text-sm text-[var(--color-text-muted)]">
            Pracownik:{" "}
            <strong className="text-[var(--color-text-primary)]">
              {ag.employee_detail.full_name}
            </strong>
          </p>
        </div>
        {canManage && isActive && (
          <Button
            variant="ghost"
            className="w-full sm:w-auto justify-center"
            onClick={() => {
              if (
                confirm(
                  "Rozwiązać tę umowę? Pracownik straci uprawnienie do bycia podpisującym (chyba że ma inną aktywną UR).",
                )
              ) {
                terminate(ag.id);
              }
            }}
            disabled={terminating}
          >
            <XCircle size={14} /> Rozwiąż UR
          </Button>
        )}
      </section>

      {/* Dane umowy */}
      <section className="bg-white dark:bg-gray-800 border border-[var(--color-border)] p-4 sm:p-6">
        <h2 className="text-sm font-mono uppercase tracking-wider mb-3 sm:mb-4 text-[var(--color-text-secondary)]">
          Dane umowy
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-4 sm:gap-6 text-sm">
          <Info label="Data podpisania" value={formatDate(ag.signed_date)} />
          <Info label="Data ważności" value={formatDate(ag.valid_until)} />
          <Info label="Tryb rozliczenia" value={ag.is_b2b ? "B2B" : "Pełnomocnictwo etatowe"} />
          <Info
            label="Status"
            value={isActive ? "Aktywna" : "Rozwiązana"}
            tone={isActive ? "ok" : "muted"}
          />
          {ag.is_b2b && (
            <>
              <Info label="Nazwa firmy" value={ag.company_name} />
              <Info label="NIP firmy" value={ag.company_nip} />
            </>
          )}
          {ag.terminated_at && (
            <Info
              label="Rozwiązano"
              value={new Date(ag.terminated_at).toLocaleString("pl-PL")}
            />
          )}
        </div>

        {ag.notes && (
          <div className="mt-5 sm:mt-6 pt-4 border-t border-[var(--color-border)]">
            <h3 className="text-xs uppercase tracking-wider text-[var(--color-text-muted)] mb-2">
              Uwagi
            </h3>
            <p className="text-sm whitespace-pre-line">{ag.notes}</p>
          </div>
        )}
      </section>

      {/* Typy podpisu */}
      <section className="bg-white dark:bg-gray-800 border border-[var(--color-border)] p-4 sm:p-6">
        <SignatureTypesTable
          agreementId={ag.id}
          readOnly={!canManage || !isActive}
        />
      </section>
    </div>
  );
}

function Info({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "ok" | "muted";
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
        {label}
      </div>
      <div
        className={
          tone === "ok"
            ? "text-green-700 dark:text-green-400 font-medium"
            : tone === "muted"
              ? "text-[var(--color-text-muted)]"
              : "text-[var(--color-text-primary)]"
        }
      >
        {value || "—"}
      </div>
    </div>
  );
}
